/**
 * Created by piotr.pozniak@thebeaverhead.com on 09/06/2021.
 */
import { dceDebug } from "../helpers/debug";

export const isCTAllowed = (current, allowed) => {
  return allowed.indexOf(current) >= 0;
};

/**
 *
 * @param widget
 * @param definitions
 * @returns {{values: {}, definitions: *}}
 */
export const initBuilder = (widget, definitions, settingsType = "design") => {
  let _values = {};

  const integrationType = widget.integration.type;
  const integrationSettings = widget.integration.settings;
  const template = widget.template;

  const widgetCreatedTimestamp = Number.parseInt(
    widget.created || new Date().getTime() / 1000
  );

  const _definitions = definitions
    .map((i) => {
      const groups = i.groups
        .map((j) => {
          const options = j.options
            .map((option, ndx) => {
              if (
                !isCTAllowed(template, option.templates) ||
                (option.integrations &&
                  !isCTAllowed(integrationType, option.integrations))
              ) {
                return null;
              }

              if (option.integrationSettings) {
                let meetsAllSettings = true;
                for (const key in option.integrationSettings) {
                  if (
                    !integrationSettings ||
                    (integrationSettings &&
                      integrationSettings[key] !==
                        option.integrationSettings[key])
                  ) {
                    meetsAllSettings = false;
                    break;
                  }
                }

                if (!meetsAllSettings) {
                  return null;
                }
              }

              if (option.applicable) {
                if (option.applicable !== settingsType) {
                  return null;
                }
              }

              _values[option.name] = {
                style: option.style,
                show: option.show,
                name: option.name,
                defaultValue: option.defaultValue,
                templates: option.templates,
                controlKind: option.control && option.control.kind,
              };

              return option;
            })
            .filter((i) => i)
            .filter((i) => {
              if (!i.version) {
                return true;
              }
              if (i.version.created === "before") {
                return Number.parseInt(i.version.value) > widgetCreatedTimestamp
                  ? true
                  : false;
              }
            });

          if (options.length) {
            return {
              ...j,
              options,
            };
          }
          return null;
        })
        .filter((i) => i);

      if (groups.length) {
        return {
          ...i,
          groups,
        };
      }

      return null;
    })
    .filter((i) => i);

  return {
    definitions: _definitions,
    values: _values,
  };
};

/**
 *
 * @returns {*[]}
 */
export const getDefinitions = (widget, settingsName = "design") => {
  return widget ? widget.builder[settingsName].definitions : [];
};

/**
 *
 * @param widget
 * @param settingsName
 * @returns {{}}
 */
export const getAllOptionValues = (widget, settingsName = "design") => {
  const definitions = getDefinitions(widget, settingsName);
  const builderValues = widget?.builder?.[settingsName]?.values || {};

  const optionValues = {};
  definitions.map((definition) => {
    definition.groups.map((group) => {
      group.options.map((option) => {
        const builderValue = builderValues[option.name];
        const compoundSuffixes =
          builderValue && COMPOUND_SUBFIELD_SUFFIXES[builderValue.controlKind];

        // Compound options (typography, surface) never store a value
        // under their own bare name — only the shared {name}Option
        // toggle and the flat {name}<Suffix> sub-fields are ever
        // written. This is the same gap filterAvailableValuesForWidgetType
        // had: reading option.name here always resolves to undefined for
        // these, and the actual saved fontSize/color/bg/etc. sub-fields
        // were never being exposed to WidgetSettingsContext at all — so
        // the stylesheet (which reads widget_settings directly, not
        // through this function) applied correctly, but the builder UI,
        // which does read through this function, never saw the saved
        // per-field values and silently fell back to schema defaults.
        if (compoundSuffixes) {
          const optionOptionValue = useOptionValue(
            widget,
            option.name + "Option",
            undefined,
            settingsName
          );

          if (optionOptionValue !== undefined) {
            optionValues[option.name + "Option"] = optionOptionValue;
          }

          Object.values(compoundSuffixes).forEach((suffix) => {
            const fieldName = option.name + suffix;

            const fieldValue = useOptionValue(
              widget,
              fieldName,
              undefined,
              settingsName
            );

            if (fieldValue !== undefined) {
              optionValues[fieldName] = fieldValue;
            }

            const fieldLinkedValue = useOptionValue(
              widget,
              fieldName + "Linked",
              undefined,
              settingsName
            );

            if (fieldLinkedValue !== undefined) {
              optionValues[fieldName + "Linked"] = fieldLinkedValue;
            }
          });

          return;
        }

        optionValues[option.name] = useOptionValue(
          widget,
          option.name,
          null,
          settingsName
        );

        const optionOptionValue = useOptionValue(
          widget,
          option.name + "Option",
          undefined,
          settingsName
        );

        if (optionOptionValue !== undefined) {
          optionValues[option.name + "Option"] = optionOptionValue;
        }

        const linkedOptionValue = useOptionValue(
          widget,
          option.name + "Linked",
          undefined,
          settingsName
        );

        if (linkedOptionValue !== undefined) {
          optionValues[option.name + "Linked"] = linkedOptionValue;
        }
      });
    });
  });

  return optionValues;
};

/**
 *
 * @param widgetModel
 * @param name
 * @param overwriteDefaultValue
 * @param settingsName
 * @returns {*|null}
 */
export const useOptionValue = (
  widgetModel,
  name,
  overwriteDefaultValue = null,
  settingsName = "design"
) => {
  if (!widgetModel || !widgetModel.builder) {
    return null;
  }

  const prop = widgetModel.widget_settings[settingsName] || {};

  if (prop[name] !== undefined) {
    if (!isNaN(prop[name]) && typeof prop[name] !== "string") {
      return Number.parseInt(prop[name]);
    }
    return prop[name];
  }

  const _values = widgetModel.builder[settingsName].values;

  if (overwriteDefaultValue === null && _values[name] === undefined) {
    return null;
  }

  return overwriteDefaultValue || _values[name].defaultValue;
};

/**
 *
 * @param widgetType
 * @returns {null}
 */
const findAppStylesheet = (widgetType) => {
  for (let i = 0; i < document.styleSheets.length; i++) {
    let style = null;

    try {
      const dummy = document.styleSheets[i].cssRules.length;
      style = document.styleSheets[i];
    } catch (e) {
      // ...
    }

    if (!style) {
      continue;
    }

    for (let j = 0; j < style.cssRules.length; j++) {
      if (style.cssRules[j].selectorText === `.dce_${widgetType}_stylesheet`) {
        return style;
      }
    }
  }

  return null;
};

/**
 *
 * @param name
 * @param defaultValue
 * @param settings
 * @returns {*}
 */
export const valueIfAvailable = (name, defaultValue, settings) => {
  if (!settings || settings[name] == undefined) {
    return defaultValue;
  }

  if (!isNaN(settings[name])) {
    return Number.parseInt(settings[name]);
  }
  return settings[name];
};

/**
 *
 * @param option
 * @param styleSheet
 * @param settings
 * @param widgetUUID
 * @param widgetType
 */
const modifyStylesheet = (
  option,
  styleSheet,
  settings,
  widgetUUID,
  widgetType
) => {
  let value = option.defaultValue;

  if (
    valueIfAvailable(option.name + "Option", "default", settings) !== "default"
  ) {
    value = settings[option.name];
  }

  const styles = Array.isArray(option.style) ? option.style : [option.style];

  const widgetSelector = `.dce_${widgetType}__${widgetUUID}`;

  styles.forEach((style) => {
    const selectors = Array.isArray(style.selector)
      ? style.selector
      : [style.selector];
    const properties = Array.isArray(style.property)
      ? style.property
      : [style.property];

    selectors.map((s) => {
      let selector =
        typeof s == "function" ? s(widgetUUID) : widgetSelector + " " + s;

      properties.map((p) =>
        changeStylesheetRule(styleSheet, selector, p, value)
      );
    });
  });
};

/**
 * Sub-field naming convention used by the "typography" compound option
 * (see TypographySetup.js) — one shared "{name}Option" default/custom
 * toggle gates all seven flat sub-fields below.
 */
const TYPOGRAPHY_SUBFIELD_SUFFIXES = {
  fontSize: "FontSize",
  fontWeight: "FontWeight",
  lineHeight: "LineHeight",
  letterSpacing: "LetterSpacing",
  textTransform: "TextTransform",
  color: "Color",
  colorHover: "ColorHover",
};

/**
 * Resolves one typography sub-value, honoring the single shared
 * "{name}Option" toggle rather than a per-subfield one.
 * @param option
 * @param key
 * @param settings
 * @returns {*}
 */
const getTypographyValue = (option, key, settings) => {
  const fieldName = option.name + TYPOGRAPHY_SUBFIELD_SUFFIXES[key];
  const isCustom =
    valueIfAvailable(option.name + "Option", "default", settings) === "custom";

  if (isCustom && settings[fieldName] !== undefined) {
    return settings[fieldName];
  }

  return option.defaultValue ? option.defaultValue[key] : undefined;
};

/**
 * Applies a "typography" compound option to the stylesheet.
 *
 * Every sub-field is written as an independent longhand declaration
 * (font-size, font-weight, line-height, letter-spacing, text-transform,
 * color) rather than combined into a `font` shorthand — the shorthand
 * requires a font-family, and typography options don't carry their own
 * family field yet, so there's nothing valid to fill that slot with
 * (using the CSS-wide keyword `inherit` inside a shorthand is invalid
 * and gets the whole rule silently rejected). color-hover is written
 * against a distinct hover selector.
 *
 * @param option
 * @param styleSheet
 * @param settings
 * @param widgetUUID
 * @param widgetType
 */
const modifyTypographyStylesheet = (
  option,
  styleSheet,
  settings,
  widgetUUID,
  widgetType
) => {
  // Accept both `style: {...}` and `style: [{...}]` — the latter is the
  // convention every flat color/cssDimension option in the other
  // definition files already uses, so it's an easy copy-paste mistake to
  // wrap a typography/surface option's style in an array too. Only the
  // first entry is meaningful here regardless: unlike flat options,
  // compound options only ever need one base selector (+ optional
  // hoverSelector), not a list of independent style rules.
  const style =
    (Array.isArray(option.style) ? option.style[0] : option.style) || {};
  const baseSelectorDef = style.selector;

  if (!baseSelectorDef) {
    return;
  }

  const widgetSelector = `.dce_${widgetType}__${widgetUUID}`;

  const resolveSelector = (s) =>
    typeof s === "function" ? s(widgetUUID) : `${widgetSelector} ${s}`;

  const asList = (s) => (Array.isArray(s) ? s : [s]);

  const baseSelectors = asList(baseSelectorDef).map(resolveSelector);
  const hoverSelectors = style.hoverSelector
    ? asList(style.hoverSelector).map(resolveSelector)
    : baseSelectors.map((s) => `${s}:hover`);

  // Writes one property/value to every selector in the list — a single
  // selector array like the "Event details" example (five different
  // markup shapes that should all share one typography treatment) needs
  // a separate CSS rule per selector, not one comma-joined selector
  // string; changeStylesheetRule already finds-or-creates per exact
  // selector text, so looping here is simpler and more robust than
  // trying to build a valid combined selector by hand.
  const writeRule = (selectors, property, value) => {
    selectors.forEach((selector) =>
      changeStylesheetRule(styleSheet, selector, property, value)
    );
  };

  const fontSize = getTypographyValue(option, "fontSize", settings);
  const fontWeight = getTypographyValue(option, "fontWeight", settings);
  const lineHeight = getTypographyValue(option, "lineHeight", settings);
  const letterSpacing = getTypographyValue(option, "letterSpacing", settings);
  const textTransform = getTypographyValue(option, "textTransform", settings);
  const color = getTypographyValue(option, "color", settings);
  const colorHover = getTypographyValue(option, "colorHover", settings);

  // Written as independent longhand declarations rather than the `font`
  // shorthand: the shorthand requires a font-family, and combining it with
  // the CSS-wide keyword `inherit` (our only fallback, since typography
  // options don't carry their own family field yet) is invalid syntax —
  // the whole rule gets silently rejected by the browser. Longhand avoids
  // that trap entirely and doesn't need a family fallback at all; the
  // widget's own global `fontFamily` option already governs family
  // separately, same as before this option existed.
  if (fontSize) {
    writeRule(baseSelectors, "font-size", fontSize);
  }

  if (fontWeight) {
    writeRule(baseSelectors, "font-weight", fontWeight);
  }

  if (lineHeight) {
    writeRule(baseSelectors, "line-height", lineHeight);
  }

  if (letterSpacing) {
    writeRule(baseSelectors, "letter-spacing", letterSpacing);
  }

  if (textTransform) {
    writeRule(baseSelectors, "text-transform", textTransform);
  }

  if (color) {
    writeRule(baseSelectors, "color", color);
  }

  if (colorHover) {
    writeRule(hoverSelectors, "color", colorHover);
  }
};

/**
 * Sub-field naming convention used by the "surface" compound option
 * (see SurfaceSetup.js) — background + hover, border (style/width/color/
 * radius), and margin/padding. margin/padding are each stored as a single
 * 4-value shorthand string plus a companion "{name}<Suffix>Linked" flag —
 * the same convention already used by calendarPadding/cwPadding elsewhere,
 * not a new one. Everything is gated behind one shared "{name}Option"
 * default/custom toggle, same as typography.
 */
const SURFACE_SUBFIELD_SUFFIXES = {
  bg: "Bg",
  bgHover: "BgHover",
  borderStyle: "BorderStyle",
  borderWidth: "BorderWidth",
  borderColor: "BorderColor",
  borderColorHover: "BorderColorHover",
  borderRadius: "BorderRadius",
  margin: "Margin",
  padding: "Padding",
};

/**
 * Resolves one surface sub-value, honoring the single shared
 * "{name}Option" toggle rather than a per-subfield one.
 */
const getSurfaceValue = (option, key, settings) => {
  const fieldName = option.name + SURFACE_SUBFIELD_SUFFIXES[key];
  const isCustom =
    valueIfAvailable(option.name + "Option", "default", settings) === "custom";

  if (isCustom && settings[fieldName] !== undefined) {
    return settings[fieldName];
  }

  return option.defaultValue ? option.defaultValue[key] : undefined;
};

/**
 * Applies a "surface" compound option to the stylesheet. Every sub-field
 * maps to one ordinary longhand declaration; margin/padding are already
 * stored as complete shorthand strings (e.g. "8px 8px 8px 8px"), so they're
 * written as-is, same as any single-value cssBox option elsewhere.
 * background-color-hover is written against a distinct hover selector.
 *
 * @param option
 * @param styleSheet
 * @param settings
 * @param widgetUUID
 * @param widgetType
 */
const modifySurfaceStylesheet = (
  option,
  styleSheet,
  settings,
  widgetUUID,
  widgetType
) => {
  // See the identical comment in modifyTypographyStylesheet above — same
  // rationale, same fix.
  const style =
    (Array.isArray(option.style) ? option.style[0] : option.style) || {};
  const baseSelectorDef = style.selector;

  if (!baseSelectorDef) {
    return;
  }

  const widgetSelector = `.dce_${widgetType}__${widgetUUID}`;

  const resolveSelector = (s) =>
    typeof s === "function" ? s(widgetUUID) : `${widgetSelector} ${s}`;

  const asList = (s) => (Array.isArray(s) ? s : [s]);

  const baseSelectors = asList(baseSelectorDef).map(resolveSelector);
  const hoverSelectors = style.hoverSelector
    ? asList(style.hoverSelector).map(resolveSelector)
    : baseSelectors.map((s) => `${s}:hover`);

  // See the identical writeRule in modifyTypographyStylesheet — same
  // rationale: one property/value written to every selector in the list,
  // rather than trying to build one valid comma-joined selector by hand.
  const writeRule = (selectors, property, value) => {
    selectors.forEach((selector) =>
      changeStylesheetRule(styleSheet, selector, property, value)
    );
  };

  const bg = getSurfaceValue(option, "bg", settings);
  const bgHover = getSurfaceValue(option, "bgHover", settings);
  const borderStyle = getSurfaceValue(option, "borderStyle", settings);
  const borderWidth = getSurfaceValue(option, "borderWidth", settings);
  const borderColor = getSurfaceValue(option, "borderColor", settings);
  const borderColorHover = getSurfaceValue(
    option,
    "borderColorHover",
    settings
  );
  const borderRadius = getSurfaceValue(option, "borderRadius", settings);
  const margin = getSurfaceValue(option, "margin", settings);
  const padding = getSurfaceValue(option, "padding", settings);

  if (bg) {
    writeRule(baseSelectors, "background-color", bg);
  }

  if (bgHover) {
    writeRule(hoverSelectors, "background-color", bgHover);
  }

  if (borderStyle) {
    writeRule(baseSelectors, "border-style", borderStyle);
  }

  if (borderWidth) {
    writeRule(baseSelectors, "border-width", borderWidth);
  }

  if (borderColor) {
    writeRule(baseSelectors, "border-color", borderColor);
  }

  if (borderColorHover) {
    writeRule(hoverSelectors, "border-color", borderColorHover);
  }

  if (borderRadius) {
    writeRule(baseSelectors, "border-radius", borderRadius);
  }

  if (margin) {
    writeRule(baseSelectors, "margin", margin);
  }

  if (padding) {
    writeRule(baseSelectors, "padding", padding);
  }
};

/**
 *
 * @param settings
 * @param widgetSettings
 * @returns {*}
 */
export const calculateDependency = (settings, widgetSettings) => {
  const evaluated = settings
    .map((i) => {
      const dependencyFieldValue = valueIfAvailable(
        i.field,
        i.defaultValue,
        widgetSettings
      );

      if (typeof i.value == "function") {
        return i.value(dependencyFieldValue);
      }

      return { value: dependencyFieldValue === i.value, and: !i.or, or: i.or };
    })
    .reduce((a, c) => (c.or ? a || c.value : a && c.value), true);

  return evaluated;
};

/**
 *
 * @param widget
 * @param settings
 * @returns {boolean}
 */
export const updateStylesheet = (widget, settingsName = "design") => {
  const widgetType = widget.widget_type;
  const settings = widget.widget_settings[settingsName];
  const styleSheet = findAppStylesheet(widgetType);
  const builder = widget.builder[settingsName];

  if (!styleSheet) {
    return false;
  }

  const calendarSuffix = settingsName === "design" ? "" : `_${settingsName}`;

  const widgetSelector = `.dce_${widgetType}__${widget.uuid}${calendarSuffix}`;

  for (const key in builder.values) {
    const option = builder.values[key];

    if (!option.style) {
      continue;
    }

    // calculate dependency ?
    if (option.show) {
      const show = calculateDependency(option.show, settings);
      if (!show) {
        continue;
      }
    }

    const applyStylesheet =
      option.controlKind === "typography"
        ? modifyTypographyStylesheet
        : option.controlKind === "surface"
        ? modifySurfaceStylesheet
        : modifyStylesheet;

    applyStylesheet(
      option,
      styleSheet,
      settings,
      `${widget.uuid}${calendarSuffix ? " ." : ""}${calendarSuffix}`,
      // `${widget.uuid}`,
      widgetType
    );
  }

  for (const key in builder.values) {
    const option = builder.values[key];

    if (key.match(/^fontFamily.*/)) {
      const fontFamily = valueIfAvailable(key, option.defaultValue, settings);

      if (fontFamily !== "inherit") {
        loadCustomFonts(widgetSelector, fontFamily, key);
      }
    }
  }
};

/**
 *
 * @param stylesheet
 * @param selector
 * @param property
 * @param value
 * @returns {boolean}
 */
const changeStylesheetRule = (stylesheet, selector, property, value) => {
  dceDebug(
    "changeStylesheetRule(" + selector + ", " + property + ", " + value + ")"
  );
  if (!property) {
    dceDebug(
      "Property for selector " + selector + " is not defined: ",
      property,
      value
    );
    return false;
  }

  selector = (selector || "").toLowerCase();
  property = property || ""; // DO NOT lowercase
  value = value || ""; // DO NOT lowercase

  for (let i = 0; i < stylesheet.cssRules.length; i++) {
    let rule = stylesheet.cssRules[i];

    if (rule.selectorText?.toLowerCase() === selector) {
      rule.style.setProperty(property, value);
      //rule.style[property] = value;
      return true;
    }
  }

  try {
    const rule = `${selector}  {${property}: ${value}; }`;
    stylesheet.insertRule(rule, stylesheet.cssRules.length);
  } catch (e) {
    console.error(e);
  }
};

/**
 *
 * @param widgetSelector
 * @param fontFamily
 */
function loadCustomFonts(widgetSelector, fontFamily, id) {
  if (!fontFamily) {
    return;
  }

  const fontUrl =
    "https://fonts.googleapis.com/css?family=" +
    fontFamily.replace(" ", "+") +
    ":300,400,500,700,900";

  const fontId = widgetSelector.trim() + "-dce-custom-font-" + id;
  const data = document.getElementById(fontId);

  if (data && data.href == fontUrl) {
    return;
  }

  //  if (!window.__DCE__DEV__) {
  if (data) {
    data.remove();
  }
  //  }

  /*for (let i = data.length-1; i >= 0; --i) {
    data[i].remove();
  }*/

  let el = document.createElement("link");
  el.rel = "stylesheet";
  el.href = fontUrl;
  el.id = fontId;

  document.querySelector("head").appendChild(el);
}

/**
 * Maps each compound control kind to its flat sub-field suffix map, so
 * filterAvailableValuesForWidgetType (below) can handle any of them
 * generically rather than needing a special case added per kind.
 */
const COMPOUND_SUBFIELD_SUFFIXES = {
  typography: TYPOGRAPHY_SUBFIELD_SUFFIXES,
  surface: SURFACE_SUBFIELD_SUFFIXES,
};

/**
 * This function removes all values from events_setings JSON string that
 * are not present in the builder values. When cloning calendar, options from
 * a different calendar type can come across. This function removes all values
 * that are not allowed for specific calendar type (based on the builder).
 * @param widgetSettings
 * @param allowedValues
 * @returns {string}
 */
export const filterAvailableValuesForWidgetType = (
  widgetSettings,
  allowedValues
) => {
  const properCalendarSettings = {};
  Object.keys(allowedValues).map((i, idx) => {
    const option = allowedValues[i];
    const compoundSuffixes =
      option && COMPOUND_SUBFIELD_SUFFIXES[option.controlKind];

    // Compound options (typography, surface) never store a value under
    // their own bare name — only the shared {name}Option toggle and the
    // flat {name}<Suffix> sub-fields are ever written. Checking
    // widgetSettings[i] here would always be undefined for these and
    // silently drop every saved sub-field on load, so they're copied
    // explicitly instead. Each sub-field's own Linked flag (used by
    // surface's margin/padding) is carried along too, same as the plain
    // cssBox case below.
    if (compoundSuffixes) {
      if (widgetSettings[i + "Option"] !== undefined) {
        properCalendarSettings[i + "Option"] = widgetSettings[i + "Option"];
      }

      Object.values(compoundSuffixes).forEach((suffix) => {
        const fieldName = i + suffix;

        if (widgetSettings[fieldName] !== undefined) {
          properCalendarSettings[fieldName] = widgetSettings[fieldName];
        }
        if (widgetSettings[fieldName + "Linked"] !== undefined) {
          properCalendarSettings[fieldName + "Linked"] =
            widgetSettings[fieldName + "Linked"];
        }
      });

      return;
    }

    if (widgetSettings[i] !== undefined) {
      properCalendarSettings[i] = widgetSettings[i];

      if (widgetSettings[i + "Option"] !== undefined) {
        properCalendarSettings[i + "Option"] = widgetSettings[i + "Option"];
      }
      if (widgetSettings[i + "Linked"] !== undefined) {
        properCalendarSettings[i + "Linked"] = widgetSettings[i + "Linked"];
      }
    }
  });

  return properCalendarSettings;
};

window.updateStylesheet = updateStylesheet;
